import { useState } from "react";
import {
  Box,
  PageTitle,
  Text,
  Grid,
  Input,
  Select,
  Spacer,
  Tag,
  Button,
  FlexiPagination,
  DashCard,
  CardSection,
  EmptyState,
  Modal,
  ModalBody,
} from "@flexisaf/flexibull2";
import styled from "styled-components";
import { useForm } from "react-hook-form";

import Theme from "../../theme";
import useTransactionsQuery from "../../hooks/transactions/useTransactionsQuery";
import useTransactionVerify from "../../hooks/transactions/useTransactionVerify";
import SectionLoader from "../../components/custom/sectionLoader";
import { EMPTY_VALUE } from "../../utils/constants";
import moment from "moment";
import { statusColors } from "../../utils/colors";

const MAX_USERNAME_LENGTH = 22;

const getDefaultFields = () => ({
  referenceId: "",
});

const Transactions = () => {
  const {
    setLimit,
    setPage,
    setSearchText,
    page,
    limit,
    totalElements,
    searchText,
    transactions,
    loadingTransactions,
    statusFilter,
    setStatusFilter,
    isFiltering,
    filteredTransactions,
    successfulTransactions,
    pendingTransactions,
    failedTransactions,
    refetch,
  } = useTransactionsQuery();
  const [modal, setModal] = useState({
    show: false,
  });
  const { verifyTransaction, verifyingTransaction, loadingId } =
    useTransactionVerify();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      ...getDefaultFields(),
    },
  });

  if (loadingTransactions && !isFiltering)
    return <SectionLoader height="80vh" />;

  if (!transactions || !transactions.count || transactions?.count <= 0)
    return (
      <>
        <Box align="center">
          <EmptyState
            type="documents"
            title="No transactions yet"
            info="This page will be updated once there is are transactions available"
          />
        </Box>
      </>
    );

  console.log({ loadingTransactions, isFiltering });

  return (
    <Box>
      <PageTitle>
        <i className="saf-driver" />
        <Text bold title="CBT" size={Theme.H3} style={{ padding: "0 10px" }}>
          Transactions
        </Text>
      </PageTitle>
      <Grid default="1fr 1fr 1fr 1fr" gap="20px">
        <DashCard>
          <CardSection left>
            Total Transactions
            <Text bold block size="20px" color={Theme.PrimaryGrey}>
              {transactions?.count || EMPTY_VALUE}
            </Text>
          </CardSection>
        </DashCard>
        <DashCard>
          <CardSection left>
            <Text color="green">Successful Transactions</Text>

            <Text bold block size="20px" color={Theme.PrimaryGrey}>
              {successfulTransactions}
            </Text>
          </CardSection>
        </DashCard>
        <DashCard>
          <CardSection left>
            <Text color="orange">Pending Transactions</Text>
            <Text bold block size="20px" color={Theme.PrimaryGrey}>
              {pendingTransactions}
            </Text>
          </CardSection>
        </DashCard>
        <DashCard>
          <CardSection left>
            <Text color="red">Failed Transactions</Text>
            <Text bold block size="20px" color={Theme.PrimaryGrey}>
              {failedTransactions}
            </Text>
          </CardSection>
        </DashCard>
      </Grid>
      <Spacer space="40px" />

      <Box width="100%">
        <Grid default="minmax(auto, 300px) minmax(auto, 180px) auto" sm="1fr">
          <Input
            name="Search email"
            placeholder="Search email"
            iconLeft="saf-search-normal"
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
          />
          <Box>
            <Select
              value={statusFilter}
              placeholder="Status"
              options={[
                { label: "All", value: null },
                { label: "Success", value: "SUCCESS" },
                { label: "Pending", value: "PENDING" },
                { label: "Failed", value: "FAILED" },
              ]}
              onChange={(e) => setStatusFilter(e)}
            />
          </Box>

          <Box align="right">
            <Button onClick={() => setModal({ show: true })}>
              Verify Transaction
            </Button>
          </Box>
        </Grid>
        <Spacer space="40px" />
        {loadingTransactions && isFiltering ? (
          <SectionLoader height="80vh" />
        ) : (
          <>
            {(isFiltering ? filteredTransactions : transactions)?.items.length >
            0 ? (
              <>
                <Grid default="1fr 1fr 1fr 1fr" gap="15px">
                  {(isFiltering
                    ? filteredTransactions
                    : transactions
                  )?.items?.map((transaction) => {
                    const {
                      referenceId,
                      status,
                      id,
                      planType,
                      amount,
                      username,
                      createdAt,
                    } = transaction;
                    return (
                      <Transaction key={id}>
                        <div className="header">
                          <div>
                            <Text size="12px" color={Theme.PrimaryGrey}>
                              {username.length <= MAX_USERNAME_LENGTH
                                ? username
                                : `${
                                    username.substring(0, MAX_USERNAME_LENGTH) +
                                    "..."
                                  }`}
                            </Text>
                          </div>
                          <Tag
                            small
                            {...statusColors[status?.toLowerCase()]}
                            pad="0px 10px"
                          >
                            <Text style={{ textTransform: "capitalize" }} bold>
                              {status?.toLowerCase()}
                            </Text>
                          </Tag>
                        </div>

                        <div className="body">
                          <Box>
                            <Text size="10px" color={Theme.PrimaryGrey}>
                              Reference Number
                            </Text>
                            <Spacer space="5px" />
                            <Text size="12px" block>
                              {referenceId}
                            </Text>
                          </Box>
                          <Spacer space="15px" />

                          <Grid default="1fr 1fr" gap="10px">
                            <Box>
                              <Text size="10px" color={Theme.PrimaryGrey}>
                                Plan
                              </Text>
                              <Spacer space="5px" />
                              <Text
                                size="12px"
                                block
                                style={{ textTransform: "capitalize" }}
                              >
                                {planType?.toLowerCase()} Plan
                              </Text>
                            </Box>
                            <Box>
                              <Text size="10px" color={Theme.PrimaryGrey}>
                                Amount
                              </Text>
                              <Spacer space="5px" />
                              <Text size="12px" block>
                                ₦ {amount}
                              </Text>
                            </Box>
                          </Grid>
                          <Spacer space="15px" />
                          <Box>
                            <Text size="10px" color={Theme.PrimaryGrey}>
                              Date
                            </Text>
                            <Spacer space="5px" />
                            <Text size="12px" block>
                              {moment(createdAt).format("DD MMMM, YYYY")}
                            </Text>
                          </Box>
                        </div>

                        <div className="footer">
                          <Button
                            pale
                            height="30"
                            pad="0 10px"
                            style={{ fontSize: "10px" }}
                            disabled={status !== "PENDING"}
                            progress={
                              verifyingTransaction && loadingId === referenceId
                            }
                            onClick={() => {
                              verifyTransaction({
                                referenceId,
                                platform: "paystack",
                              });
                            }}
                          >
                            Verify Transaction
                          </Button>
                        </div>
                      </Transaction>
                    );
                  })}
                </Grid>
                <FlexiPagination
                  pageCounts={[
                    { value: 8, label: "8 Rows" },
                    { value: 16, label: "16 Rows" },
                    { value: 32, label: "32 Rows" },
                  ]}
                  itemsDisplayed
                  total={totalElements}
                  pageSize={limit}
                  onChange={(page) => {
                    setPage(page);
                  }}
                  changePageSize={({ value }) => {
                    setPage(1);
                    setLimit(value);
                  }}
                  current={page}
                />
              </>
            ) : (
              <>
                <Box align="center">
                  <EmptyState
                    type="documents"
                    title="No Transactions"
                    info="There are no transacitons with the specified options"
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      <Modal open={modal.show} center={false}>
        <ModalBody width="400px">
          <Box>
            <Grid default="auto max-content" responsive={false} className="bb">
              <Box pad="24px 32px">
                <Text uppercase bold>
                  Verify a transaction
                </Text>
              </Box>
            </Grid>

            <Box pad="32px" width="100%">
              <Input
                placeholder="Ex: 1600010201"
                type="number"
                value={watch("referenceId")}
                label="Enter Reference ID"
                required
                style={{ width: "100%" }}
                {...register("referenceId", {
                  required: "Reference ID is required",
                })}
                error={errors.referenceId?.message}
                onChange={({ target: { value } }) =>
                  setValue("referenceId", value)
                }
              />
            </Box>
          </Box>

          <Grid default="auto max-content" responsive={false} className="bt">
            <Box pad="24px">
              <Button
                pale
                color="rgba(0,0,0,0.0)"
                fontColor={Theme.PrimaryGrey}
                onClick={() => {
                  setModal({ show: false });
                  return reset({ ...getDefaultFields() });
                }}
              >
                Cancel
              </Button>
            </Box>
            <Box pad="24px">
              <Button
                progress={verifyingTransaction}
                onClick={handleSubmit((values) => {
                  console.log({ values });
                  verifyTransaction(
                    {
                      referenceId: values.referenceId,
                      platform: "paystack",
                    },
                    () => {
                      setValue("referenceId", "");
                      setModal({ show: false });
                      refetch();
                    }
                  );
                })}
              >
                Verify Transaction
              </Button>
            </Box>
          </Grid>
        </ModalBody>
      </Modal>
    </Box>
  );
};

export default Transactions;

const Transaction = styled.div`
  border: 1px solid ${Theme.PrimaryBorderColor};
  border-radius: 5px;

  & .header {
    display: flex;
    justify-content: space-between;
    padding: 15px;
    align-items: center;
    border-bottom: 1px solid ${Theme.PrimaryBorderColor};
  }

  & .body {
    padding: 15px;
    border-bottom: 1px solid ${Theme.PrimaryBorderColor};
  }

  & .footer {
    padding: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
